import clsx from 'clsx'
import React from 'react'
import {hasActiveStandardsFilter} from 'components/standards-selector/domain/active-filters.cjs'
import {Link, useHistory} from 'react-router-dom'
import {makeStyles} from 'external/material'
import Select from '../../select'
import SearchPattern from '../components/search-pattern'
import {orderBy} from 'external/lodash'

import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'

import ClearableSelect from 'components/clearable-select'
import { selectCourseLabel } from 'helpers/quiz'

const useStyles = makeStyles(filterStyles)

export default function AssessmentBankFilters({context}) {

  const classes = useStyles()
  const {translate, navigate, institutionCourses} = context

  const createFilterHandler = (name, mapQuery) => (event) => {
    const value = event.target.value == '' ? undefined : event.target.value
    const query = mapQuery
      ? mapQuery(context.query, value)
      : {...context.query, [name]: value}
    navigate(undefined, undefined, {query: {...query, page: 1}})
  }

  const courses = context.query.subject
    ? institutionCourses.filter(({course}) => course.substring(0,2) == context.query.subject)
    : institutionCourses

  const units = context.query.course && context.units
    ? context.units.slice(1)
    : []

  const lessons = context.query.unit && context.lessons
    ? context.lessons
    : []

  return (
    <Box className={classes.filters}>
      <Box className="top">
        <ClearableSelect
          id="assessment-bank-repository-filter"
          className={clsx(classes.select, "repository")}
          value={context.query.repository || ""}
          onChange={createFilterHandler('repository')}
          caption={translate('repository')}
        >
          <MenuItem value="dreyfous">{translate('dreyfous')}</MenuItem>
          <MenuItem value="institution">{translate('institution')}</MenuItem>
        </ClearableSelect>
        {context.quizTypes.length > 0 && (
        <ClearableSelect
          id="assessment-bank-type-filter"
          className={clsx(classes.select, "type")}
          value={context.query.type || ""}
          onChange={createFilterHandler('type')}
          caption={translate('quiz-type')}
        >
          {context.quizTypes.map((type, key) => {
            return (<MenuItem value={type.id} key={key}>{translate(`${type.name}-label`)}</MenuItem>)
          })}
        </ClearableSelect>)}
        <Box className={classes.filler} />
        <Box className={classes.pattern}>
          <SearchPattern path={context.link()} context={context} />
        </Box>
      </Box>
      {!hasActiveStandardsFilter(context.quizStandardFilters) && <Box className={classes.standardsSearch}>
        <Link to={context.link({sectionName: 'standards-search'}, '/:languageId/:role/:page/:tab/:sectionName', {preserveQuery: true})} className={classes.standardsSearchLink}>
          {translate('standards-search')}
        </Link>
      </Box>}
      <Box className="bottom">
      {context.subjects.length > 0 && (
        <ClearableSelect
          id="question-bank-subject-filter"
          className={clsx(classes.select, "subject")}
          value={context.query.subject || ""}
          onChange={createFilterHandler('subject', (query, subject) => ({...query, course: undefined, unit: undefined, lesson: undefined, subject}))}
          caption={translate('subject_label')}
        >
          {context.subjects.map((subject, key) => {
            return (<MenuItem value={subject.id} key={key}>{subject.name}</MenuItem>)
          })}
        </ClearableSelect>
      )}
      {courses.length > 0 && (
        <ClearableSelect
          id="question-bank-course-filter"
          className={clsx(classes.select, "course")}
          value={context.query.course || ""}
          onChange={createFilterHandler('course', (query, course) => {
            return course
              ? {...query, course, subject: course.substring(0,2), unit: undefined, lesson: undefined}
              : {...query, course: undefined, unit: undefined, lesson: undefined}
          })}
          caption={translate('course_assessment_header')}
        >
          {orderBy(courses, ['course']).map((course, key) => {
            return (<MenuItem value={course.course} key={key}>{selectCourseLabel(course)}</MenuItem>)
          })}
        </ClearableSelect>
      )}
      <ClearableSelect
        id="question-bank-unit-filter"
        className={clsx(classes.select, "unit")}
        value={context.query.unit || ""}
        onChange={createFilterHandler('unit', (query, unit) => {
          return unit
            ? {...query, unit}
            : {...query, unit: undefined, lesson: undefined}
        })}
        caption={translate('unitName-header')}
      >
        {units.map((unit, key) => {
          const id = unit.id.substring(7)
          const name = `${id.padStart(2, '0')} - ${unit.name}`
          return (<MenuItem value={unit.id} key={id}>{name}</MenuItem>)
        })}
      </ClearableSelect>
      <ClearableSelect
        id="question-bank-lesson-filter"
        className={clsx(classes.select, "lesson")}
        value={context.query.lesson || ""}
        onChange={createFilterHandler('lesson', (query, lesson) => {
          return lesson
            ? {...query, lesson}
            : {...query, lesson: undefined}
        })}
        caption={translate('lesson-header')}
      >
        {lessons.map((lesson, key) => {
          const name = `${(key + 1).toString().padStart(2, '0')} - ${lesson.name}`
          return (<MenuItem value={lesson.id} key={key}>{name}</MenuItem>)
        })}
      </ClearableSelect>
      </Box>
    </Box>
  )
}

function filterStyles (theme) {
  return {
    filters: {
      padding: '15px 0',
      flexGrow: 1,
      display: "flex",
      flexDirection: 'column',
      flexWrap: 'wrap',
      "& .top": {
        display: "flex",
      },
      "& .bottom": {
        marginTop: 8,
        display: "flex",
      },
    },
    standardsSearch: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 5,
      marginBottom: 10
    },
    standardsSearchLink: {
      color: '#009dd8',
      fontSize: '9.5pt'
    },
    select: {
      marginLeft: 15,
      '&:first-child': {
        marginLeft: 0
      },
      '&.repository': {
        minWidth: 155,
      },
      '&.type': {
        minWidth: 155,
      },
      '&.subject': {
        minWidth: 155
      },
      '&.course': {
        flexGrow: 1,
        minWidth: 100,
        maxWidth: 300,
      },
      '&.unit': {
        flexGrow: 1,
        minWidth: 100,
        maxWidth: 300,
      },
      '&.lesson': {
        flexGrow: 1,
        minWidth: 100,
      }
    },
    filler: {
      flexGrow: 1,
    },
    pattern: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end"
    }
  }
}

